import { fetchApi } from '../client';
import { buildQS } from '../helpers';
import type { Patient, Visit } from '../../../types';

export interface PatientPagedResponse {
  content: Patient[];
  totalElements: number;
  totalPages: number;
  page: number;
  size: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export type PatientScope = 'all' | 'own' | 'group' | 'transferred';

export interface PatientListParams {
  page?: number;
  size?: number;
  search?: string;
  gender?: string;
  sortBy?: string;
  sortDir?: string;
  patientScope?: PatientScope;
}

export interface PatientOverviewSafetyAlert {
  type: 'ALLERGY' | 'CONDITION' | 'VITAL' | 'LAB';
  message: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
}

export interface PatientOverviewVitalReading {
  systolic?: number;
  diastolic?: number;
  pulse?: number;
  temperature?: number;
  oxygenSaturation?: number;
  weight?: number;
  bmi?: number;
  recordedAt?: string;
}

export interface PatientOverviewCurrentMedication {
  name: string;
  dosage?: string;
  frequency?: string;
  duration?: string;
  prescribedDate?: string;
}

export interface PatientOverviewVisit {
  visitId: string;
  visitDate: string;
  chiefComplaint?: string;
  doctorName?: string;
}

export interface PatientOverviewMedicationItem {
  name: string;
  dosage?: string;
  frequency?: string;
  duration?: string;
  instructions?: string;
}

export interface PatientOverviewPrescription {
  id: string;
  createdAt: string;
  diagnosis?: string;
  status: string;
  doctorName?: string;
  medicationCount: number;
  medicationSummary: string[];
  medications: PatientOverviewMedicationItem[];
}

export interface PatientOverviewLabOrder {
  id: string;
  testName: string;
  status: string;
  createdAt: string;
  isAbnormal: boolean;
  /** Names the critical parameters, e.g. "Critical: Haemoglobin, Platelets". Null when none are. */
  criticalNote?: string | null;
  /**
   * Groups tests ordered in the same requisition. Tests ordered together share this, so the
   * clinician list renders one row per requisition instead of one per test.
   *
   * Null for legacy orders and for clinics with no in-house lab — those stand alone rather than
   * being merged into a single meaningless group.
   */
  accessionId?: string | null;
  accessionNumber?: string | null;
  /** Catalogue category — "Imaging", "Radiology", "Hematology"… Null when the name matches none. */
  category?: string | null;
  /**
   * True when this order is an imaging study rather than bloodwork.
   *
   * Imaging is not a separate order type in ClinVea: an ultrasound is a lab test definition whose
   * category is Imaging, ordered down the same pipeline. This flag is what lets the clinician view
   * separate the two.
   */
  imaging?: boolean;
}

export interface PatientOverviewFollowUp {
  id: string;
  followUpDate: string;
  status: string;
  instructions?: string;
}

export interface PatientOverviewFollowUpSummary {
  pending: PatientOverviewFollowUp[];
  missed: PatientOverviewFollowUp[];
  nextUpcoming?: PatientOverviewFollowUp;
  totalPending: number;
  totalMissed: number;
}

export interface PatientOverviewResponse {
  safetyAlerts: PatientOverviewSafetyAlert[];
  activeConditions: string[];
  vitalsTrend: PatientOverviewVitalReading[];
  currentMedications: PatientOverviewCurrentMedication[];
  recentVisits: PatientOverviewVisit[];
  recentPrescriptions: PatientOverviewPrescription[];
  recentLabOrders: PatientOverviewLabOrder[];
  followUpSummary: PatientOverviewFollowUpSummary;
}

export interface MedicationSnapshot {
  name: string;
  dosage?: string;
  frequency?: string;
  duration?: string;
}

export interface VitalsSnapshot {
  systolicBP?: number;
  diastolicBP?: number;
  pulse?: number;
  temperature?: number;
  spO2?: number;
  weight?: number;
  respiratoryRate?: number;
  date?: string;
}

export interface PatientClinicalSnapshot {
  allergies?: string;
  bloodGroup?: string;
  chronicConditions?: string;
  lastDiagnosis?: string;
  chiefComplaint?: string;
  clinicalNotes?: string;
  prescriptionDate?: string;
  medications?: MedicationSnapshot[];
  lastVitals?: VitalsSnapshot;
}

export const patientsModule = {
  /** Paginated patient directory — used by PatientSearch. */
  list: (params?: PatientListParams) =>
    fetchApi<PatientPagedResponse>(`/api/patients${buildQS(params)}`),

  /** Legacy full-list used by appointment booking and other selects. */
  getAll: async () => {
    const res = await fetchApi<PatientPagedResponse>('/api/patients?size=1000');
    if (res.error) return { error: res.error };
    return { data: (res.data?.content ?? []) as Patient[] };
  },

  search: (query: string) =>
    fetchApi<Patient[]>(`/api/patients/search?q=${encodeURIComponent(query)}`),

  searchByMobile: async (mobile: string) => {
    const response = await fetchApi<Patient[]>(
      `/api/patients/search?q=${encodeURIComponent(mobile)}`
    );
    return response.data?.find(p => p.mobile === mobile) ?? null;
  },

  getById: (id: string) =>
    fetchApi<Patient>(`/api/patients/${id}`),

  getByIds: (ids: string[]) =>
    fetchApi<Patient[]>('/api/patients/batch', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    }),

  create: (patientData: Partial<Patient>) =>
    fetchApi<Patient>('/api/patients', {
      method: 'POST',
      body: JSON.stringify(patientData),
    }),

  update: (id: string, patientData: Partial<Patient>) =>
    fetchApi<Patient>(`/api/patients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patientData),
    }),

  invite: (id: string, data: { email: string; redirectUrl: string }) =>
    fetchApi<{ invitationId: string; email: string; expiresAt: string }>(
      `/api/patients/${id}/invite`,
      { method: 'POST', body: JSON.stringify(data) }
    ),

  getLabOrders: (patientId: string) =>
    fetchApi<any[]>(`/api/patients/${patientId}/lab-orders`),

  /**
   * Order lab tests directly, with no prescription.
   *
   * Every test in one call lands on a single accession, so the lab receives one requisition and the
   * clinician sees one record. Ordering tests one at a time would create unrelated requisitions.
   *
   * `labId` is optional — omitted, the backend routes to the clinic's in-house lab.
   */
  createLabOrders: (patientId: string, body: { tests: string[]; labId?: string; instructions?: string }) =>
    fetchApi<{ count: number; accessionId: string; orderIds: string[] }>(
      `/api/patients/${patientId}/lab-orders`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  getVisits: (patientId: string) =>
    fetchApi<Visit[]>(`/api/patients/${patientId}/visits`),

  getOverview: (patientId: string) =>
    fetchApi<{ data: PatientOverviewResponse }>(`/api/patients/${patientId}/overview`),

  /** General clinical snapshot (allergies, meds, last diagnosis/vitals) — not itself a referral/
   *  transfer concept, just consumed by the "Clinical Summary" step of that workflow. */
  getClinicalSnapshot: (patientId: string) =>
    fetchApi<PatientClinicalSnapshot>(`/api/patients/${patientId}/clinical-snapshot`),
};
